import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { useAuth } from '../AuthContext';
import { useSubscription } from '../useSubscription';
import Paywall from '../Paywall';
import AuthModal from '../components/AuthModal';
import CheckoutModal from '../components/CheckoutModal';
import stripePromise from '../stripePromise';
import './Pick3Page.css';

const PLAN_META = {
  'price_1TKOZvILOpB6RcrOkie5R5Kb': { label: 'Yearly plan',  price: '$24.99 / year'  },
  'price_1TKORnILOpB6RcrOkFwJKC0e': { label: 'Monthly plan', price: '$3.99 / month' },
};

const PRO_TABS = ['Numbers', 'Fireball'];

const PAGE_SIZE = 50;

function cryptoRandom() {
  return crypto.getRandomValues(new Uint32Array(1))[0] / (2 ** 32);
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// Count-up animation hook
function useCountUp(target, duration = 1400) {
  const [count, setCount] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    if (!target) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return count;
}

const TABS = ['Digits', 'Numbers', 'History', 'Picker', 'Fireball'];
const DRAW_TYPES = ['All', 'Day', 'Eve'];

export default function Pick3Page() {
  const { session }             = useAuth();
  const { isPro, refetch: refetchSub } = useSubscription();
  const [allDraws, setAllDraws] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [drawType, setDrawType] = useState('All');
  const [tab,      setTab]      = useState('Digits');
  const [page,     setPage]     = useState(0);
  const [picks,    setPicks]    = useState([]);
  const [lookupDigits, setLookupDigits] = useState([0, 0, 0]);
  const [showPaywall,     setShowPaywall]     = useState(false);
  const [showAuthModal,   setShowAuthModal]   = useState(null);
  const [checkout,        setCheckout]        = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const openUpgradeFlow = useCallback(() => {
    if (!session) setShowAuthModal('signin');
    else setShowPaywall(true);
  }, [session]);

  const handleSelectPlan = useCallback(async (priceId, promotionCodeId = null) => {
    if (!session) return;
    setShowPaywall(false);
    setCheckoutLoading(true);
    try {
      const res  = await fetch('/api/create-subscription.php', {
        method:  'POST',
        headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ price_id: priceId }),
      });
      const data = await res.json();
      if (data.clientSecret) {
        setCheckout({ clientSecret: data.clientSecret, priceId, promotionCodeId });
      }
    } catch (err) {
      console.error('[checkout] Network error:', err);
    } finally {
      setCheckoutLoading(false);
    }
  }, [session]);

  const handleCheckoutSuccess = useCallback(() => {
    setCheckout(null);
    refetchSub();
  }, [refetchSub]);

  const handleTabClick = useCallback((t) => {
    if (PRO_TABS.includes(t) && !isPro) {
      openUpgradeFlow();
    } else {
      setTab(t);
      setPage(0);
    }
  }, [isPro, openUpgradeFlow]);

  // ── Fetch ──────────────────────────────────────────────────
  useEffect(() => {
    fetch('/api/pick3-data.php')
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        setAllDraws(data.draws || []);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // ── Filter ─────────────────────────────────────────────────
  const draws = useMemo(() => {
    if (drawType === 'All') return allDraws;
    return allDraws.filter(d => d.type === drawType);
  }, [allDraws, drawType]);

  // ── Latest draw ────────────────────────────────────────────
  const latestDraw = draws[0] || null;
  const latestIsToday = latestDraw?.date === todayStr();

  // ── Digit frequency by position ────────────────────────────
  const posFreq = useMemo(() => {
    const freq = [0, 1, 2].map(() =>
      Object.fromEntries([...Array(10).keys()].map(i => [i, 0]))
    );
    draws.forEach(d => {
      d.digits.forEach((digit, pos) => {
        freq[pos][digit] = (freq[pos][digit] || 0) + 1;
      });
    });
    return freq;
  }, [draws]);

  // ── Overall digit frequency ────────────────────────────────
  const overallFreq = useMemo(() => {
    const freq = Object.fromEntries([...Array(10).keys()].map(i => [i, 0]));
    draws.forEach(d => {
      d.digits.forEach(digit => { freq[digit] = (freq[digit] || 0) + 1; });
    });
    return freq;
  }, [draws]);

  // ── Fireball frequency ─────────────────────────────────────
  const fireballFreq = useMemo(() => {
    const freq = Object.fromEntries([...Array(10).keys()].map(i => [i, 0]));
    draws.forEach(d => {
      if (d.fireball !== null && d.fireball !== undefined) {
        freq[d.fireball] = (freq[d.fireball] || 0) + 1;
      }
    });
    return freq;
  }, [draws]);

  const fireballDrawCount = useMemo(() =>
    draws.filter(d => d.fireball !== null && d.fireball !== undefined).length,
    [draws]
  );

  // ── 3-digit combo frequency ────────────────────────────────
  const numFreq = useMemo(() => {
    const freq = {};
    draws.forEach(d => {
      const key = d.digits.map(n => String(n)).join('');
      freq[key] = (freq[key] || 0) + 1;
    });
    return freq;
  }, [draws]);

  const sortedNums = useMemo(() =>
    Object.entries(numFreq)
      .map(([num, count]) => ({ num, count }))
      .sort((a, b) => b.count - a.count),
    [numFreq]
  );

  // ── Derived stats ──────────────────────────────────────────
  const maxPosFreq  = useMemo(() => posFreq.map(pf => Math.max(...Object.values(pf))), [posFreq]);
  const maxOverall  = useMemo(() => Math.max(...Object.values(overallFreq)), [overallFreq]);
  const maxFireball = useMemo(() => Math.max(...Object.values(fireballFreq)), [fireballFreq]);
  const fmtCombo = (num) => num.split('').join('-');
  const mostCommonNum   = sortedNums[0]?.num ? fmtCombo(sortedNums[0].num) : '—';
  const mostCommonDigit = useMemo(() =>
    Object.entries(overallFreq).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—',
    [overallFreq]
  );

  // ── Count-up targets ───────────────────────────────────────
  const animatedTotal  = useCountUp(draws.length);
  const animatedUnique = useCountUp(Object.keys(numFreq).length);

  // ── Picker ─────────────────────────────────────────────────
  const [pickMode, setPickMode] = useState('random');

  const generatePick = useCallback(() => {
    if (pickMode === 'weighted' && draws.length > 0) {
      const result = [0, 1, 2].map(pos => {
        const freq = posFreq[pos];
        const total = Object.values(freq).reduce((a, b) => a + b, 0);
        let rand = cryptoRandom() * total;
        for (let d = 0; d <= 9; d++) {
          rand -= (freq[d] || 0);
          if (rand <= 0) return d;
        }
        return 0;
      });
      setPicks(result);
    } else {
      setPicks([0, 1, 2].map(() => Math.floor(cryptoRandom() * 10)));
    }
  }, [pickMode, draws.length, posFreq]);

  useEffect(() => { if (draws.length) generatePick(); }, [draws.length, pickMode]); // eslint-disable-line

  // ── Combination lookup ─────────────────────────────────────
  const lookupKey    = lookupDigits.join('');
  const lookupCount  = numFreq[lookupKey] || 0;
  const lookupLast   = useMemo(() =>
    draws.find(d => d.digits.join('') === lookupKey),
    [draws, lookupKey]
  );
  const lookupRate   = draws.length > 0 ? ((lookupCount / draws.length) * 100).toFixed(2) : '0.00';

  const cycleDigit = (pos, dir) => {
    setLookupDigits(prev => {
      const next = [...prev];
      next[pos] = (next[pos] + dir + 10) % 10;
      return next;
    });
  };

  // ── History pagination ─────────────────────────────────────
  const totalPages = Math.ceil(draws.length / PAGE_SIZE);
  const pageDraws  = draws.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const firstDate = draws[draws.length - 1]?.date;
  const lastDate  = draws[0]?.date;

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="p3-page">
      <Seo
        path="/pick3"
        title="Pick 3 Analysis — LottoPickle"
        description="Digit frequency and draw analysis tools for Pick 3 lottery games. Explore patterns in historical Pick 3 results with LottoPickle."
      />

      <div className="p3-header">
        <div className="p3-header-left">
          <Link to="/" className="p3-logo">LottoPickle</Link>
          <span className="p3-game-badge">NC Pick 3</span>
          {draws.length > 0 && (
            <span className="p3-date-range">
              {formatDate(firstDate)} – {formatDate(lastDate)}
            </span>
          )}
        </div>
        <div className="p3-header-right">
          <Link to="/app" className="p3-nav-link">Powerball &amp; Mega</Link>
          {session
            ? <Link to="/account" className="p3-nav-link">Account</Link>
            : <Link to="/pricing" className="p3-nav-link">Sign in</Link>
          }
        </div>
      </div>

      {loading && (
        <div className="p3-loading">
          <div className="p3-spinner" />
          <span>Loading Pick 3 data…</span>
        </div>
      )}

      {error && (
        <div className="p3-error-box">
          <p className="p3-error-msg">{error}</p>
          {error.toLowerCase().includes('not found') && (
            <p className="p3-error-hint">
              Download the CSV from nclottery.com/pick3-download and upload it to
              <code> server-api/data/pick3.csv</code> on the server.
            </p>
          )}
        </div>
      )}

      {!loading && !error && draws.length > 0 && (
        <>
          {/* Draw type filter */}
          <div className="p3-filter-row">
            {DRAW_TYPES.map(t => (
              <button
                key={t}
                className={`p3-filter-btn ${drawType === t ? 'active' : ''}`}
                onClick={() => { setDrawType(t); setPage(0); }}
              >
                {t === 'Eve' ? 'Evening' : t}
              </button>
            ))}
          </div>

          {/* Latest Draw spotlight */}
          {latestDraw && (
            <div className="p3-latest-wrap">
              <div className="p3-latest-card">
                <div className="p3-latest-meta">
                  <span className="p3-latest-label">Latest Draw</span>
                  <span className={`p3-type-tag ${latestDraw.type === 'Day' ? 'day' : 'eve'}`}>
                    {latestDraw.type}
                  </span>
                  <span className="p3-latest-date">
                    {latestIsToday ? 'Today' : formatDate(latestDraw.date)}
                  </span>
                  {latestIsToday && <span className="p3-live-badge">LIVE</span>}
                </div>
                <div className="p3-latest-balls">
                  {latestDraw.digits.map((digit, i) => (
                    <div key={i} className="p3-latest-ball">{digit}</div>
                  ))}
                  {latestDraw.fireball !== null && latestDraw.fireball !== undefined && (
                    <>
                      <div className="p3-latest-sep">+</div>
                      <div className="p3-latest-ball fireball">{latestDraw.fireball}</div>
                      <span className="p3-latest-fb-label">Fireball</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="p3-stats-row">
            {[
              { label: 'Total draws',         val: animatedTotal.toLocaleString() },
              { label: 'Most common combo',   val: mostCommonNum },
              { label: 'Most common digit',   val: mostCommonDigit },
              { label: 'Unique combos drawn', val: animatedUnique.toLocaleString() },
            ].map(s => (
              <div className="p3-stat" key={s.label}>
                <div className="p3-stat-label">{s.label}</div>
                <div className="p3-stat-val">{s.val}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="p3-tabs">
            {TABS.map(t => {
              const locked = PRO_TABS.includes(t) && !isPro;
              return (
                <button
                  key={t}
                  className={`p3-tab ${tab === t ? 'active' : ''} ${locked ? 'p3-tab-locked' : ''}`}
                  onClick={() => handleTabClick(t)}
                >
                  {t}
                  {locked && <span className="p3-tab-lock">🔒</span>}
                </button>
              );
            })}
          </div>

          {/* ── Digits tab ─────────────────────────────────── */}
          {tab === 'Digits' && (
            <div className="p3-digits-wrap">
              <DigitChart title="Overall — all positions combined" freq={overallFreq} maxVal={maxOverall} colorCls="p3-bar-overall" />
              <DigitChart title="1st digit" freq={posFreq[0]} maxVal={maxPosFreq[0]} colorCls="p3-bar-pos0" />
              <DigitChart title="2nd digit" freq={posFreq[1]} maxVal={maxPosFreq[1]} colorCls="p3-bar-pos1" />
              <DigitChart title="3rd digit" freq={posFreq[2]} maxVal={maxPosFreq[2]} colorCls="p3-bar-pos2" />
            </div>
          )}

          {/* ── Numbers tab ────────────────────────────────── */}
          {tab === 'Numbers' && (
            <div className="p3-numbers-wrap">
              <div className="p3-num-cols">
                <NumTable title="Most frequent" rows={sortedNums.slice(0, 25)} maxCount={sortedNums[0]?.count} />
                <NumTable title="Least frequent" rows={[...sortedNums].reverse().slice(0, 25)} maxCount={sortedNums[0]?.count} />
              </div>
              <p className="p3-note">{sortedNums.length.toLocaleString()} unique combinations drawn from 1,000 possible (000–999).</p>
            </div>
          )}

          {/* ── History tab ────────────────────────────────── */}
          {tab === 'History' && (
            <div className="p3-history-wrap">
              <p className="p3-history-meta">
                {draws.length.toLocaleString()} draws — page {page + 1} of {totalPages}
              </p>
              <table className="p3-history-table">
                <thead>
                  <tr><th>Date</th><th>Draw</th><th>Combination</th></tr>
                </thead>
                <tbody>
                  {pageDraws.map((d, i) => (
                    <tr key={i} className={d.type === 'Day' ? 'p3-row-day' : 'p3-row-eve'}>
                      <td className="p3-hist-date">{formatDate(d.date)}</td>
                      <td>
                        <span className={`p3-type-tag ${d.type === 'Day' ? 'day' : 'eve'}`}>
                          {d.type}
                        </span>
                      </td>
                      <td>
                        <div className="p3-draw-balls">
                          {d.digits.map((digit, j) => (
                            <span key={j} className="p3-ball">{digit}</span>
                          ))}
                          {d.fireball !== null && d.fireball !== undefined && (
                            <span className="p3-ball p3-ball-fb">{d.fireball}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="p3-pagination">
                  <button className="p3-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
                  <span className="p3-page-info">{page + 1} / {totalPages}</span>
                  <button className="p3-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
              )}
            </div>
          )}

          {/* ── Picker tab ─────────────────────────────────── */}
          {tab === 'Picker' && (
            <div className="p3-picker-wrap">

              {/* Disclaimer banner */}
              <div className="p3-picker-disclaimer">
                <span className="p3-disclaimer-icon">⚠️</span>
                <span>
                  <strong>Important:</strong> No method — including frequency-weighted — can predict Pick 3 outcomes.
                  Every draw is an independent random event. Historical frequency does <em>not</em> improve your odds of winning.
                  This tool is for data exploration and entertainment only.
                </span>
              </div>

              <div className="p3-pick-section">
                <h3 className="p3-pick-section-title">Number Generator</h3>

                {/* Mode toggle */}
                <div className="p3-pick-mode-toggle">
                  <button
                    className={`p3-mode-btn ${pickMode === 'random' ? 'active' : ''}`}
                    onClick={() => setPickMode('random')}
                  >
                    True Random
                  </button>
                  <button
                    className={`p3-mode-btn ${pickMode === 'weighted' ? 'active' : ''}`}
                    onClick={() => setPickMode('weighted')}
                  >
                    Frequency-Weighted
                  </button>
                </div>

                <p className="p3-mode-desc">
                  {pickMode === 'random'
                    ? 'Each digit (0–9) has an equal 10% chance of being selected for each position.'
                    : 'Each digit\'s selection probability is proportional to how often it has appeared in that position historically. Differences from true random are small by design — Pick 3 is a fair game.'}
                </p>

                <div className="p3-pick-balls">
                  {picks.map((d, i) => (
                    <div key={i} className={`p3-pick-ball ${pickMode === 'weighted' ? 'weighted' : ''}`}>{d}</div>
                  ))}
                </div>
                <button className="p3-pick-btn" onClick={generatePick}>
                  Generate new pick
                </button>
              </div>

              <div className="p3-lookup-section">
                <h3 className="p3-pick-section-title">Combination Lookup</h3>
                <p className="p3-lookup-desc">Select any combination to see its draw history.</p>
                <div className="p3-lookup-selectors">
                  {[0, 1, 2].map(pos => (
                    <div key={pos} className="p3-lookup-digit-col">
                      <button className="p3-lookup-arrow" onClick={() => cycleDigit(pos, 1)}>▲</button>
                      <div className="p3-lookup-digit">{lookupDigits[pos]}</div>
                      <button className="p3-lookup-arrow" onClick={() => cycleDigit(pos, -1)}>▼</button>
                    </div>
                  ))}
                </div>
                <div className="p3-lookup-combo">{fmtCombo(lookupKey)}</div>
                <div className="p3-lookup-results">
                  {lookupCount === 0 ? (
                    <div className="p3-lookup-never">
                      <span className="p3-lookup-zero">0</span>
                      <span className="p3-lookup-zero-label">Never drawn in {draws.length.toLocaleString()} recorded draws</span>
                    </div>
                  ) : (
                    <div className="p3-lookup-stats">
                      <div className="p3-lookup-stat">
                        <div className="p3-lookup-stat-val">{lookupCount}×</div>
                        <div className="p3-lookup-stat-label">Times drawn</div>
                      </div>
                      <div className="p3-lookup-stat">
                        <div className="p3-lookup-stat-val">{lookupRate}%</div>
                        <div className="p3-lookup-stat-label">Appearance rate</div>
                      </div>
                      <div className="p3-lookup-stat">
                        <div className="p3-lookup-stat-val">{lookupLast ? formatDate(lookupLast.date) : '—'}</div>
                        <div className="p3-lookup-stat-label">Last drawn</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Fireball tab ────────────────────────────────── */}
          {tab === 'Fireball' && (
            <div className="p3-fireball-wrap">
              <div className="p3-fireball-intro">
                <p className="p3-fireball-desc">
                  The Fireball is drawn separately from the main Pick 3 combination.
                  Players can use it to replace one of the drawn digits to create winning combinations.
                  Analyzed across <strong>{fireballDrawCount.toLocaleString()}</strong> draws.
                </p>
              </div>
              <DigitChart
                title="Fireball digit frequency"
                freq={fireballFreq}
                maxVal={maxFireball}
                colorCls="p3-bar-fireball"
              />
              <div className="p3-fireball-table-wrap">
                <div className="p3-fireball-table-title">Fireball digit breakdown</div>
                <table className="p3-fireball-table">
                  <thead>
                    <tr><th>Digit</th><th>Drawn</th><th>Rate</th><th></th></tr>
                  </thead>
                  <tbody>
                    {[...Array(10).keys()]
                      .map(d => ({ d, count: fireballFreq[d] || 0 }))
                      .sort((a, b) => b.count - a.count)
                      .map(({ d, count }) => (
                        <tr key={d}>
                          <td className="p3-fb-digit">{d}</td>
                          <td className="p3-fb-count">{count.toLocaleString()}</td>
                          <td className="p3-fb-rate">
                            {fireballDrawCount > 0
                              ? ((count / fireballDrawCount) * 100).toFixed(1) + '%'
                              : '—'}
                          </td>
                          <td className="p3-fb-bar-cell">
                            <div
                              className="p3-fb-bar"
                              style={{ width: `${maxFireball > 0 ? (count / maxFireball) * 100 : 0}%` }}
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="p3-disclaimer">
            For entertainment and data analysis purposes only. Pick 3 draws are independent random events — historical frequency has no bearing on future results. Not gambling advice.
          </p>
        </>
      )}

      {/* Paywall */}
      {showPaywall && (
        <Paywall onSelectPlan={handleSelectPlan} onClose={() => setShowPaywall(false)} />
      )}

      {/* Auth modal */}
      {showAuthModal && (
        <AuthModal
          initialMode={showAuthModal}
          reason="Create a free account to subscribe"
          onSuccess={async (newSession) => {
            setShowAuthModal(null);
            if (newSession?.access_token) {
              const res  = await fetch('/api/subscription-status.php', {
                headers: { Authorization: 'Bearer ' + newSession.access_token },
              });
              const data = await res.json();
              if (!data.isPro) setShowPaywall(true);
              refetchSub();
            }
          }}
          onClose={() => setShowAuthModal(null)}
        />
      )}

      {/* Checkout modal */}
      {checkout && (
        <CheckoutModal
          stripePromise={stripePromise}
          clientSecret={checkout.clientSecret}
          priceId={checkout.priceId}
          accessToken={session?.access_token}
          promotionCodeId={checkout.promotionCodeId}
          planLabel={PLAN_META[checkout.priceId]?.label ?? 'Premium'}
          planPrice={PLAN_META[checkout.priceId]?.price ?? ''}
          onSuccess={handleCheckoutSuccess}
          onClose={() => setCheckout(null)}
        />
      )}

      {checkoutLoading && (
        <div className="checkout-loading-overlay">
          <div className="spinner" />
          <span>Setting up your subscription…</span>
        </div>
      )}

      <footer className="p3-footer">
        <div className="p3-footer-links">
          <Link to="/">Home</Link>
          <Link to="/app">Powerball &amp; Mega</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/support">Support</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </div>
        <p className="p3-footer-note">Must be 18+. For entertainment only. Not affiliated with NC Lottery.</p>
      </footer>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────

function DigitChart({ title, freq, maxVal, colorCls }) {
  const values  = [...Array(10).keys()].map(d => freq[d] || 0);
  const minVal  = Math.min(...values);
  const range   = maxVal - minVal || 1;

  return (
    <div className="p3-digit-chart">
      <div className="p3-chart-title">{title}</div>
      <div className="p3-digit-bars">
        {[...Array(10).keys()].map(d => {
          const count = freq[d] || 0;
          const pct   = 25 + ((count - minVal) / range) * 75;
          return (
            <div key={d} className="p3-bar-col">
              <div className="p3-bar-track">
                <div className={`p3-bar-fill ${colorCls}`} style={{ height: `${pct}%` }}>
                  <span className="p3-bar-inner-digit">{d}</span>
                </div>
              </div>
              <div className="p3-bar-count">{count.toLocaleString()}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NumTable({ title, rows, maxCount }) {
  return (
    <div className="p3-num-table-wrap">
      <div className="p3-num-table-title">{title}</div>
      <table className="p3-num-table">
        <thead>
          <tr><th>#</th><th>Combination</th><th>Drawn</th><th></th></tr>
        </thead>
        <tbody>
          {rows.map(({ num, count }, i) => (
            <tr key={num}>
              <td className="p3-num-rank">{i + 1}</td>
              <td className="p3-num-val">{num.split('').join('-')}</td>
              <td className="p3-num-count">{count}×</td>
              <td className="p3-num-bar-cell">
                <div className="p3-num-bar" style={{ width: `${(count / maxCount) * 100}%` }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
